/**
 * Image validation and URL helper utilities
 * Used across email components to safely handle image values from API responses
 */

/**
 * Validates if an image value is valid (string URL, File, or Blob)
 * Returns null for invalid values like empty objects, undefined, etc.
 * 
 * @param {*} img - The image value to validate
 * @returns {string|File|Blob|null} - The validated image or null if invalid
 */
export const validateImage = (img) => {
  if (!img) return null;
  // Valid if it's a string (URL or base64)
  if (typeof img === 'string') return img;
  // Valid if it's a File or Blob
  if (img instanceof File || img instanceof Blob) return img;
  // Invalid if it's an empty object or non-image object
  if (typeof img === 'object') {
    if (Object.keys(img).length === 0) return null;
  }
  // Default to null for any other invalid type
  return null;
};

/**
 * Safely gets an image URL from various image value types
 * Handles string URLs, File/Blob objects, and invalid values (empty objects, null, undefined)
 * 
 * @param {*} img - The image value to get URL from
 * @returns {string|null} - The image URL or null if invalid
 */
export const getImageUrl = (img) => {
  if (!img) return null;
  // If it's a string (URL or base64), return it directly
  if (typeof img === 'string') {
    // Handle placeholder text from API
    if (img === 'Insert_Image_URL_or_Alt_Text') return null;
    return img;
  }
  // If it's a File or Blob, create an object URL
  if (img instanceof File || img instanceof Blob) {
    return URL.createObjectURL(img);
  }
  // If it's an empty object or any other invalid type, return null
  if (typeof img === 'object' && Object.keys(img).length === 0) {
    return null;
  }
  // For any other case, return null to show placeholder
  return null;
};

/**
 * Checks if a value is a valid image (string URL, File, or Blob)
 * 
 * @param {*} img - The value to check
 * @returns {boolean} - True if valid image, false otherwise
 */
export const isValidImage = (img) => {
  if (!img) return false;
  if (typeof img === 'string') return true;
  if (img instanceof File || img instanceof Blob) return true;
  // Empty objects or other invalid types
  if (typeof img === 'object' && Object.keys(img).length === 0) return false;
  return false;
};

/**
 * Cleans up invalid image values in an object
 * Useful when processing API responses that may contain empty objects for images
 * 
 * @param {*} value - The value to clean
 * @returns {string|File|Blob|null} - Cleaned value or null if invalid
 */
export const cleanImageValue = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  if (value instanceof File || value instanceof Blob) return value;
  // If it's an object, check if it's empty or invalid
  if (typeof value === 'object') {
    if (Object.keys(value).length === 0) return null;
  }
  return null;
};

